<template>
  <div class="novel-book-root">
    <!-- 画像エリア -->
    <div class="image-area">
      <router-link
        class="novel-book"
        :to="{ name: 'editor', params: { novelId: novel.novelId } }"
        :class="{ 'tutorial-novel': novel.title === 'はじめてのNola' }"
      >
        <image-view
          class="novel-image"
          :key="coverImageVersion"
          :src="getImageKey"
          placeholder="/img/placeholders/novel.png"
        />
      </router-link>

      <!-- ピンアイコン（常時表示） -->
      <div class="pinned" @click="onClickPinned">
        <IconPin class="pin-icon" :class="{ active: novel.pinned }" />
      </div>

      <!-- 三点メニューボタン -->
      <div class="dots-wrapper">
        <button class="dots-container" @click="toggleMenu">
          <IconVerticalEllipsis class="icon-18" />
        </button>

        <!-- ポップオーバーメニュー -->
        <div v-if="isMenuOpen" class="menu-popover">
          <button class="menu-item" @click="showCopyNovelDialog">
            <IconCopy class="menu-item-icon" />
            <span>{{ $t("homePage.novelList.listItem.copyNovel") }}</span>
          </button>
          <button class="menu-item" @click="showGroupingDialog">
            <IconFolderHeart class="menu-item-icon" />
            <span>{{ $t("homePage.novelList.listItem.grouping") }}</span>
          </button>
          <button class="menu-item" @click="importNovel">
            <IconUpload class="menu-item-icon" />
            <span>{{ $t("homePage.novelList.listItem.importManuscript") }}</span>
          </button>
          <button class="menu-item menu-item-danger" @click="deleteNovel">
            <IconTrash class="menu-item-icon" />
            <span>{{ $t("homePage.novelList.listItem.deleteNovel") }}</span>
          </button>
        </div>
      </div>

      <input style="display: none" ref="input" type="file" accept=".txt,.docx" multiple @change="onSelectFile()" />
    </div>

    <!-- メニュー外クリックで閉じる -->
    <div v-if="isMenuOpen" class="menu-backdrop" @click.stop="toggleMenu" />

    <!-- メタ情報（画像の外） -->
    <div class="novel-meta">
      <router-link class="novel-title-link" :to="{ name: 'editor', params: { novelId: novel.novelId } }">
        <div class="novel-title line-clamp">{{ novel.title }}</div>
      </router-link>
      <div class="novel-info-row">
        <div class="novel-dates">
          <span class="novel-date">
            <IconReload class="date-icon" />
            {{ toUpdatedAt }}
          </span>
          <span class="novel-date">
            <IconPencil class="date-icon" />
            {{ toCreatedAt }}
          </span>
        </div>
        <span v-if="workStatusLabel" class="novel-status-badge">{{ workStatusLabel }}</span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import novelItemMixin from "@/mixins/novelItemMixin";
import ImageView from "@/components/atoms/ImageView.vue";
import IconPin from "~icons/figma/Pin";
import IconVerticalEllipsis from "~icons/figma/VerticalEllipsis";
import IconCopy from "~icons/figma/Copy";
import IconUpload from "~icons/figma/Upload";
import IconTrash from "~icons/figma/Trash";
import IconFolderHeart from "~icons/figma/FolderHeart";
import IconReload from "~icons/figma/Reload";
import IconPencil from "~icons/figma/Pencil";

export default novelItemMixin.extend({
  components: {
    ImageView,
    IconPin,
    IconVerticalEllipsis,
    IconCopy,
    IconUpload,
    IconTrash,
    IconFolderHeart,
    IconReload,
    IconPencil,
  },
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";
.novel-book-root {
  position: relative;
  box-sizing: border-box;
  width: 100%;
}

/* アイコンサイズユーティリティ */
.icon-18 {
  font-size: 18px;
}

.icon-20 {
  font-size: 20px;
}

/* 画像エリアラッパー（pin/dots/overlay/menuの位置基準） */
.image-area {
  position: relative;
  width: 100%;
}

/* 画像リンク */
.novel-book {
  display: block;
  position: relative;
  width: 100%;
  padding-bottom: 141%;
  overflow: hidden;
  box-sizing: border-box;

  &:hover {
    box-shadow: var(--shadow-sm);
  }

  &:active {
    box-shadow: var(--shadow-md);
  }
}

.novel-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* ピンアイコン（常時表示） */
.pinned {
  @include hover-overlay;
  position: absolute;
  top: 6px;
  left: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: var(--radius-rounded);
  background-color: var(--color-background-normal);
  box-shadow: var(--shadow-sm);
  cursor: pointer;
  z-index: 2;
  overflow: hidden;

  .pin-icon {
    font-size: var(--font-size-md);
    color: var(--color-text-low);

    &.active {
      color: var(--color-warning);
    }
  }
}

/* 三点メニューボタン（画像右下） */
.dots-wrapper {
  position: absolute;
  bottom: 8px;
  right: 6px;
  z-index: 3;
}

.dots-container {
  @include hover-overlay;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: var(--radius-rounded);
  background-color: var(--color-background-normal);
  color: var(--color-text-low);
  border: none;
  cursor: pointer;
  box-shadow: var(--shadow-sm);
  overflow: hidden;
}

/* メタ情報エリア（画像の外） */
.novel-meta {
  padding: 8px 2px 0;
}

.novel-title-link {
  text-decoration: none;
  color: var(--color-text-normal);
}

.novel-title {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);
  line-height: var(--line-height-default);
  word-wrap: break-word;

  @include mq(sp) {
    font-size: var(--font-size-xxs);
  }
}

.line-clamp {
  display: -webkit-box;
  overflow: hidden;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
}

/* 日付 + ステータス行 */
.novel-info-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
  gap: 4px;
}

.novel-dates {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.novel-date {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: var(--font-size-xxs);
  line-height: 1;
  color: var(--color-text-low);
  white-space: nowrap;
  margin-bottom: 2px;

  .date-icon {
    font-size: var(--font-size-xs);
    flex-shrink: 0;
  }
}

.novel-status-badge {
  display: inline-block;
  padding: 2px 8px;
  font-size: var(--font-size-xxs);
  line-height: 14px;
  font-weight: var(--font-weight-bold);
  border-radius: var(--radius-rounded);
  background-color: var(--color-background-invert);
  color: var(--color-text-invert);
  white-space: nowrap;
  flex-shrink: 0;
  text-align: center;
}

/* ポップオーバーメニュー */
.menu-popover {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  min-width: 180px;
  background-color: var(--color-background-normal);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-md);
  padding: 4px 0;
  z-index: 10;
  white-space: nowrap;
}

.menu-item {
  position: relative;
  @include hover-overlay;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 16px;
  border: none;
  background: transparent;
  color: var(--color-text-normal);
  cursor: pointer;
  font-size: var(--font-size-xs);


  &.menu-item-danger {
    color: var(--color-warning);
  }
}

.menu-item-icon {
  font-size: var(--font-size-lg);
  flex-shrink: 0;
}

/* メニュー外クリック用の透明背景 */
.menu-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}
</style>
